import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, FileSpreadsheet, Plus, Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { generateFlightDocument, getFlightDocTimes } from "@/lib/api";

const b64ToBlobUrl = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const MANIFEST_ROLES = ["Pilot", "Crew", "Passenger"];
const GENDEC_ROLES = ["Crew", "PAX"];
const GENDERS = ["", "M", "F", "X"];

const emptyPerson = (role) => ({
  role: role || "",
  name: "",
  passport: "",
  nationality: "",
  phone: "",
  expiry: "",
  gender: "",
  dob: "",
  pob: "",
});

// Build initial rows from the flight's crew/pax counts.
const initialPersons = (flight, docType) => {
  const crew = Math.max(0, parseInt(flight.crew_number, 10) || 0);
  const pax = Math.max(0, parseInt(flight.pax_out, 10) || 0);
  const rows = [];
  if (docType === "manifest") {
    rows.push(emptyPerson("Pilot"));
    for (let i = 1; i < crew; i++) rows.push(emptyPerson("Crew"));
    for (let i = 0; i < pax; i++) rows.push(emptyPerson("Passenger"));
  } else {
    for (let i = 0; i < crew; i++) rows.push(emptyPerson("Crew"));
    for (let i = 0; i < pax; i++) rows.push(emptyPerson("PAX"));
  }
  if (rows.length === 0) rows.push(emptyPerson(docType === "manifest" ? "Passenger" : "PAX"));
  return rows;
};

export function FlightDocButton({ flight, docType, variant = "icon" }) {
  const isManifest = docType === "manifest";
  const roles = isManifest ? MANIFEST_ROLES : GENDEC_ROLES;
  const [open, setOpen] = useState(false);
  const [persons, setPersons] = useState([]);
  const [extra, setExtra] = useState({});
  const [saving, setSaving] = useState(false);

  const handleOpen = () => {
    setPersons(initialPersons(flight, docType));
    setExtra(
      isManifest
        ? { aircraft_task: flight.flight_type || "", planned_departure: flight.time || "" }
        : { departure_loc: "", departure_utc: "", arrival_loc: "", arrival_utc: "" }
    );
    setOpen(true);
    if (!isManifest) {
      // Auto-fill departure/arrival UTC & LOCAL date-times from the flight route.
      getFlightDocTimes(flight.id)
        .then((res) => setExtra((prev) => ({ ...prev, ...res.data })))
        .catch(() => {});
    }
  };

  const updatePerson = (idx, key, value) => {
    setPersons((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  };
  const addPerson = () => setPersons((prev) => [...prev, emptyPerson(isManifest ? "Passenger" : "PAX")]);
  const removePerson = (idx) => setPersons((prev) => prev.filter((_, i) => i !== idx));

  const handleGenerate = async () => {
    setSaving(true);
    try {
      const res = await generateFlightDocument(flight.id, { doc_type: docType, persons, extra });
      const url = b64ToBlobUrl(res.data.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.open(url, "_blank");
      toast.success(`${isManifest ? "Manifest" : "GENDEC"} generated & saved to history`);
      setOpen(false);
    } catch (e) {
      toast.error("Could not generate the document");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-8 text-xs bg-white dark:bg-[#0f172a]";
  const selectCls =
    "h-8 text-xs rounded-md border border-input bg-white dark:bg-[#0f172a] px-2 text-[#0f172a] dark:text-white";

  const Icon = isManifest ? FileText : FileSpreadsheet;
  const label = isManifest ? "Manifest" : "GENDEC";

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10 h-8 w-8"
          title={`Generate ${label}`}
          data-testid={`${docType}-button-${flight.id}`}
        >
          <Icon size={16} />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpen}
          className="flex items-center gap-2 text-[#0f172a] border-[#d4af37] hover:bg-[#d4af37] hover:text-white"
          data-testid={`${docType}-button-${flight.id}`}
        >
          <Icon size={14} />
          {label}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-4xl bg-white dark:bg-[#1e293b] max-h-[88vh] overflow-y-auto"
          data-testid={`${docType}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] dark:text-white flex items-center gap-2">
              <Icon size={22} className="text-[#d4af37]" />
              {isManifest ? "Passenger Manifest & Safety Briefing" : "General Declaration (GENDEC)"}
              <span className="text-sm font-normal text-[#64748b] dark:text-gray-400">
                {flight.flight_number} · {(flight.origin || "").toUpperCase()} → {(flight.destination || "").toUpperCase()}
              </span>
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-[#64748b] dark:text-gray-400 -mt-2">
            Personal data is used only to generate this PDF and is <b>not stored</b> on the flight. Rows are
            pre-created from the crew ({flight.crew_number || 0}) and PAX ({flight.pax_out || 0}) counts.
          </p>

          {/* Extra / header fields */}
          {isManifest ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748b] dark:text-gray-400">Aircraft Task</label>
                <Input
                  className={inputCls}
                  value={extra.aircraft_task || ""}
                  onChange={(e) => setExtra({ ...extra, aircraft_task: e.target.value })}
                  data-testid="manifest-aircraft-task"
                />
              </div>
              <div>
                <label className="text-xs text-[#64748b] dark:text-gray-400">Planned Time of Departure</label>
                <Input
                  className={inputCls}
                  value={extra.planned_departure || ""}
                  onChange={(e) => setExtra({ ...extra, planned_departure: e.target.value })}
                  data-testid="manifest-planned-departure"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["departure_utc", "Departure UTC"],
                ["departure_loc", "Departure LOC"],
                ["arrival_utc", "Arrival UTC"],
                ["arrival_loc", "Arrival LOC"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-xs text-[#64748b] dark:text-gray-400">{lbl}</label>
                  <Input
                    className={inputCls}
                    value={extra[k] || ""}
                    onChange={(e) => setExtra({ ...extra, [k]: e.target.value })}
                    data-testid={`gendec-${k.replace("_", "-")}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Persons */}
          <div className="space-y-2 mt-2">
            {persons.map((p, idx) => (
              <div
                key={idx}
                className="flex flex-wrap items-end gap-2 p-2 rounded-lg border border-[#e2e8f0] dark:border-slate-700"
                data-testid={`${docType}-person-row-${idx}`}
              >
                <div className="w-6 text-center text-xs text-[#94a3b8] pb-2">{idx + 1}</div>
                <div>
                  <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Role</label>
                  <select
                    className={selectCls}
                    value={p.role}
                    onChange={(e) => updatePerson(idx, "role", e.target.value)}
                    data-testid={`${docType}-role-${idx}`}
                  >
                    {roles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Name</label>
                  <Input className={inputCls} value={p.name} onChange={(e) => updatePerson(idx, "name", e.target.value)} data-testid={`${docType}-name-${idx}`} />
                </div>
                <div className="w-28">
                  <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Passport</label>
                  <Input className={inputCls} value={p.passport} onChange={(e) => updatePerson(idx, "passport", e.target.value)} data-testid={`${docType}-passport-${idx}`} />
                </div>

                {!isManifest && (
                  <>
                    <div className="w-24">
                      <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Expiry</label>
                      <Input className={inputCls} placeholder="MM/YYYY" value={p.expiry} onChange={(e) => updatePerson(idx, "expiry", e.target.value)} data-testid={`gendec-expiry-${idx}`} />
                    </div>
                    <div className="w-16">
                      <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Gender</label>
                      <select className={selectCls} value={p.gender} onChange={(e) => updatePerson(idx, "gender", e.target.value)} data-testid={`gendec-gender-${idx}`}>
                        {GENDERS.map((g) => (<option key={g} value={g}>{g || "—"}</option>))}
                      </select>
                    </div>
                    <div className="w-28">
                      <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Date of birth</label>
                      <Input className={inputCls} placeholder="DD/MM/YYYY" value={p.dob} onChange={(e) => updatePerson(idx, "dob", e.target.value)} data-testid={`gendec-dob-${idx}`} />
                    </div>
                    <div className="w-28">
                      <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Place of birth</label>
                      <Input className={inputCls} value={p.pob} onChange={(e) => updatePerson(idx, "pob", e.target.value)} data-testid={`gendec-pob-${idx}`} />
                    </div>
                  </>
                )}

                <div className="w-20">
                  <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Nationality</label>
                  <Input className={inputCls} value={p.nationality} onChange={(e) => updatePerson(idx, "nationality", e.target.value)} data-testid={`${docType}-nationality-${idx}`} />
                </div>

                {isManifest && (
                  <div className="w-28">
                    <label className="text-[10px] text-[#64748b] dark:text-gray-400 block">Phone</label>
                    <Input className={inputCls} value={p.phone} onChange={(e) => updatePerson(idx, "phone", e.target.value)} data-testid={`manifest-phone-${idx}`} />
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removePerson(idx)}
                  title="Remove"
                  data-testid={`${docType}-remove-${idx}`}
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={addPerson}
            className="w-fit flex items-center gap-1 text-[#0f172a] dark:text-white"
            data-testid={`${docType}-add-person`}
          >
            <Plus size={14} /> Add person
          </Button>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${docType}-cancel`}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] flex items-center gap-2"
              data-testid={`${docType}-generate`}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
